import React, { useLayoutEffect, useState } from 'react'

import { Box, Stack, Fade, useTheme, alpha, keyframes } from '@mui/material'

import { useFillBottom } from 'hooks'

import VLogoSVG from 'assets/images/vlogo.svg'

const spin = keyframes`to { transform: rotate(360deg); }`

interface LoaderProps {
  /** Kept for the existing call sites; the stage centres either way. */
  center?: boolean
  loading?: boolean
  sx?: object
  /**
   * Diameter of the ring in PIXELS. This used to be a `vw` multiplier, which made the
   * loader enormous on a wide monitor and small on a laptop — a loading indicator is
   * chrome, not content, so it should be one size everywhere.
   */
  size?: number
}

/**
 * The platform's page loader: the Vera mark inside an indeterminate arc.
 *
 * REPLACES a 157KB animated GIF. Three things were wrong with it, and each is why
 * this looks the way it does:
 *   - It was a fixed-colour raster, so it was the one element in the app that ignored
 *     dark mode. This is CSS + the mark asset, and the mark inverts on dark.
 *   - It was sized in `vw`, so it scaled with the viewport. Now a fixed px diameter.
 *   - It ran a 2000/2800ms `gif → v → logo` phase machine whose v and logo branches
 *     were commented out: the timers fired, the state changed, and nothing ever
 *     rendered differently. There is no phase machine here, and nothing is timed —
 *     the loader shows for exactly as long as the work takes.
 *
 * Chosen from five candidates in /internal/labs/loaders. The arc won on the grounds
 * that this is chrome a brand sees dozens of times a day while working, so it should
 * recede rather than perform — and because it matches the certificate scan loader, so
 * a customer tapping a tag and a brand loading their dashboard see one company. The
 * more expressive options (particles, radar) are kept in Labs; the brand moment
 * belongs on surfaces seen rarely, not on a table refresh.
 *
 * No artificial minimum duration. A loader that outlives its wait makes a fast page
 * feel slow, which is the trap the old 3.8s LogoLoader fell into on the scan path.
 */
export const Loader = ({ loading = true, sx, size = 48 }: LoaderProps) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  /**
   * ## Why this measures instead of just saying `min-height: 100%`
   *
   * It used to say exactly that, and `min-height: 100%` NEVER RESOLVED. A percentage
   * min-height resolves against the PARENT's computed `height`, and every call site's
   * parent is a plain page-root `Box` with `height: auto` — so it computes to `auto`
   * and the stage collapses to its 48px content. Measured on /settings/users:
   * `min-height` computed `"100%"`, used height `48px`, and the parent Box measured
   * 109px tall (i.e. hugging the header + this loader) inside a 688px content area.
   *
   * The visible consequence, and the bug this fixes: the mark rendered at y=173 on
   * /settings/users while the table's empty state sits at y≈493 — 320px apart, at the
   * TOP of the page rather than in it. And because the failure is silent, the position
   * was decided by whichever call site happened to wrap the loader in its OWN centring
   * box: /settings/addressbook and /settings/billing/payment-methods put a
   * `minHeight: '55vh'` flex box around it and landed at y=434 / y=421, while the bare
   * call sites landed at 173. One component, three different anchors.
   *
   * So: fill from this stage's own top down to the page's bottom gutter — the same
   * 24px the L/R gutter uses (§22) — via the same `useFillBottom` the shared `Table`
   * uses for its grid (§28/§36: reuse the measurement, don't hand-roll a fourth one).
   *
   * ## The guard is load-bearing — do not remove it
   *
   * `fill` is decided ONCE, on mount, by asking whether the parent HUGS this stage
   * (our bottom is flush with the parent's) or already gives it room. It has to be a
   * one-shot decision, because in a parent that CENTRES us (the `55vh` boxes above)
   * growing the stage moves the stage's own top, which changes the measured fill,
   * which grows it again — that feedback loop diverges and the page would creep. So a
   * call site that already sizes the loader keeps its own geometry untouched, and only
   * the collapsed case is corrected.
   */
  const [fill, setFill] = useState(false)
  const { ref: stageRef, height: fillHeight } = useFillBottom<HTMLDivElement>({
    enabled: fill,
    minHeight: 0,
    deps: [loading],
  })

  useLayoutEffect(() => {
    const el = stageRef.current
    const parent = el?.parentElement
    if (!el || !parent) return
    // Flush bottoms => the parent's height is our content's height => nothing is
    // sizing us, so we must size ourselves. 1px of slack for sub-pixel layout.
    setFill(parent.getBoundingClientRect().bottom - el.getBoundingClientRect().bottom <= 1)
    // Mount-only by design (see the guard note above) — re-running this on every
    // layout is precisely the feedback loop the guard exists to avoid.
  }, [])
  const stroke = theme.palette.text.primary
  // r is inset by the stroke so the ring never clips its own edge at any size.
  const r = 21
  const markSize = Math.round(size * 0.4)

  return (
    <Fade in={loading} timeout={180}>
      <Stack
        ref={stageRef}
        sx={{
          // Translucent so the page underneath stays legible as context rather than
          // being replaced by a blank panel.
          background: alpha(theme.palette.background.default, 0.5),
          // `100%` is kept as the pre-measure value only. It is the same non-resolving
          // declaration described above, so it is a no-op in a block parent and the
          // stage stays 48px for the one layout pass before `fillHeight` arrives —
          // which is a LAYOUT effect, so it lands before paint and there is no visible
          // jump. Where a parent DOES give it a definite height, this is what makes it
          // stretch, and `fill` stays false so the measurement never touches it.
          minHeight: fill && fillHeight != null ? fillHeight : '100%',
          zIndex: 10,
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          ...sx,
        }}
      >
        <Box sx={{ position: 'relative', width: size, height: size, display: 'grid', placeItems: 'center' }}>
          <Box
            component="svg"
            viewBox="0 0 48 48"
            aria-hidden
            sx={{
              position: 'absolute',
              width: size,
              height: size,
              animation: `${spin} 1.1s linear infinite`,
              // A spinner is the one thing that must not spin for someone who asked
              // for less motion; the static track still reads as "busy".
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            {/* Full track, so the arc reads as travelling AROUND something. Without it
                a lone arc looks like a stray stroke beside the mark rather than a
                position on a ring. */}
            <circle cx="24" cy="24" r={r} fill="none" strokeWidth={2} stroke={stroke} strokeOpacity={0.12} />
            <circle
              cx="24"
              cy="24"
              r={r}
              fill="none"
              strokeWidth={2}
              strokeLinecap="round"
              stroke={stroke}
              strokeOpacity={0.9}
              // Fixed ~30% arc. Animating the dash length as well made it shrink to a
              // few percent, at which point it stopped reading as a spinner.
              strokeDasharray="40 92"
            />
          </Box>

          <Box
            component="img"
            src={VLogoSVG}
            alt=""
            aria-hidden
            sx={{
              width: markSize,
              height: 'auto',
              display: 'block',
              // The asset is a fixed dark mark; invert it on dark so this is not the
              // one element that ignores the theme.
              ...(isDark && { filter: 'brightness(0) invert(1)' }),
            }}
          />
        </Box>
      </Stack>
    </Fade>
  )
}
